/* eslint-disable indent */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { FC } from 'react';
import styles from './usosBim.module.scss';
import { UsoBIM } from '../../../../types/ObraDetails';

import { useTranslation } from 'react-i18next';
import TitleHelp from '../../components/titleHelp/titleHelp';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { ReactComponent as checkTrue } from '../../../../assets/check_true.svg';
import { SvgIcon } from '@mui/material';
import LevelBar from '../components/levelBar/LevelBar';
import { LEVEL_BAR_COLORS } from '../colorSchema';


interface UsosBimProps {
  usages: UsoBIM[]
}

interface UsageRow extends UsoBIM {
  etapas: UsoBIM['etapa'][];
}

const usageOrder = (usage: UsoBIM): number => {
  if (Number.isFinite(usage.codigo)) return usage.codigo;

  const code = usage.descripcion.match(/\bUB(\d+)\b/i);
  return code ? Number(code[1]) : Number.MAX_SAFE_INTEGER;
};

const UsosBim: FC<UsosBimProps> = (props) => {
  const { usages } = props;
  const [t] = useTranslation();

  const groupByUsosBim = (results: UsoBIM[]) => {
    const groupedResults = results.reduce<Record<string, UsageRow>>((groups, item) => {

     // Group by description instead of codigo to handle cases where
     // the same BIM use has different codes but same description with different stages
     const key = `${item.nivel}-${item.descripcion}`;
     const etapas = groups[key]?.etapas || [];

     // Avoid duplicate stages
     const existingEtapa = etapas.find((e) => e.codigo === item.etapa.codigo);
     if (!existingEtapa) {
       etapas.push(item.etapa);
     }

     groups[key] = { ...item, etapas };
      return groups;
    }, {});
    return Object.values(groupedResults)
      .sort((first, second) => usageOrder(first) - usageOrder(second));
  };

  const rows = groupByUsosBim(usages);

  const check = (value: boolean) => {
    return <div className={styles.check}>
      {value && <SvgIcon component={checkTrue} inheritViewBox />}
    </div>;
  };

  const getLevel = (level: string) => {
    if (!level) {
      return 0;
    }
    if (level.toLowerCase() === 'muy bajo') {
      return 0;
    }
    else if (level.toLowerCase() === 'bajo') {
      return 1;
    }
    else if (level.toLowerCase() === 'medio') {
      return 2;
    }
    else if (level.toLowerCase() === 'alto') {
      return 3;
    }
    else if (level.toLowerCase() === 'muy alto') {
      return 4;
    }
    return 0;
  };

  const levelBar = (level: string, i: number) => {
    const style = LEVEL_BAR_COLORS[getLevel(level)];
    return <div className={styles.levelContainer}>
     <div
        className={styles.levelDescription}
        style={{
          color: style.color,
          backgroundColor: style.backgroundColor,
          width: level ? undefined : '210px',
        }}
      >
        {level ? t('details.bim.' + level) : t('details.notInformed')}
      </div>


      <LevelBar
        identifier={`level-${i}`}
        color={{ fairColor: style.backgroundColor, darkColor: style.color }}
        height={12}
        level={getLevel(level)} />
    </div>;
  };

  return (
    <div
      data-testid="usos-bim-test"
      className={styles.bimContainer} >


      <TitleHelp
        id='usos-bim'
        value={t('details.bim.usosBim')}
        help={t('help.bim.bimUses')}
      />
      <TableContainer component={Paper} className={styles.bimTable}
        sx={{
          '&.MuiPaper-root': {
            borderRadius: '0 !important',
            padding: '0px 30px',
            boxShadow: 'none',
            width: 'calc(100% - 60px)',
          }
        }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align='center' className={styles.accionaUseColumn}>{t('details.bim.accionaUse')}</TableCell>
              <TableCell align='center' className={styles.levelColumn}>{t('details.bim.level')}</TableCell>
              <TableCell align='center' className={styles.checkColumn}>{t('details.bim.bid')}</TableCell>
              <TableCell align='center' className={styles.checkColumn}>{t('details.bim.design')}</TableCell>
              <TableCell align='center' className={styles.checkColumn}>{t('details.bim.construction')}</TableCell>
              <TableCell align='center' className={styles.checkColumn}>{t('details.bim.operation')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i: number) => (
              <TableRow
                key={`row-${i}`}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell align='left'>
                  {row.descripcion}
                </TableCell>
                <TableCell align='center'>{levelBar(row.nivel, i)}</TableCell>
                <TableCell align='center'>{check(!!row.etapas.find((e) => e.codigo === 1))}</TableCell>
                <TableCell align='center'>{check(!!row.etapas.find((e) => e.codigo === 2))}</TableCell>
                <TableCell align='center'>{check(!!row.etapas.find((e) => e.codigo === 0))}</TableCell>
                <TableCell align='center'>{check(!!row.etapas.find((e) => e.codigo === 3))}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>


    </div >);
};

export default UsosBim;
